import { useEffect, useRef, useCallback } from 'react';
import { Recipe } from '../../types/recipe';
import { LoadingSpinner } from '../LoadingSpinner';
import { ErrorMessage } from '../ErrorMessage';

interface RecipeSelectorProps {
  isOpen: boolean;
  recipes: Recipe[] | null;
  isLoading: boolean;
  error: string | null;
  onSelect: (recipe: Recipe) => void;
  onClose: () => void;
}

export function RecipeSelector({
  isOpen,
  recipes,
  isLoading,
  error,
  onSelect,
  onClose,
}: RecipeSelectorProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the close button when the dialog opens
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const focusableElements = dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement | undefined;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;

        if (!firstElement || !lastElement) return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  const getCannabinoidInfo = (recipe: Recipe): string | null => {
    const infused = recipe.ingredients.filter((ing) => ing.isCannabisInfused);
    if (infused.length === 0) return null;

    const types = [...new Set(infused.map((ing) => ing.cannabinoidType).filter(Boolean))];
    if (types.length === 0 && !recipe.perServingMg) return null;

    const parts: string[] = [];
    if (types.length > 0) parts.push(types.join(', '));
    if (recipe.perServingMg != null) parts.push(`${recipe.perServingMg} mg/serving`);

    return parts.join(' · ');
  };

  return (
    <div className="confirm-dialog-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="confirm-dialog recipe-selector"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recipe-selector-title"
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="recipe-selector-header">
          <h2 id="recipe-selector-title" className="confirm-dialog-title">
            Select a Recipe
          </h2>
          <button
            ref={closeButtonRef}
            className="recipe-selector-close"
            onClick={onClose}
            type="button"
            aria-label="Close recipe selector"
          >
            ✕
          </button>
        </div>

        <div className="recipe-selector-content">
          {isLoading && <LoadingSpinner />}

          {error && <ErrorMessage message={error} />}

          {!isLoading && !error && recipes && recipes.length === 0 && (
            <p className="recipe-selector-empty">No recipes found. Create a recipe first.</p>
          )}

          {!isLoading && !error && recipes && recipes.length > 0 && (
            <ul className="recipe-selector-list" role="listbox" aria-label="Available recipes">
              {recipes.map((recipe) => {
                const cannabinoidInfo = getCannabinoidInfo(recipe);
                return (
                  <li key={recipe.recipeId} role="option" aria-selected={false}>
                    <button
                      className="recipe-selector-item"
                      type="button"
                      onClick={() => onSelect(recipe)}
                    >
                      <span className="recipe-selector-item-title">{recipe.title}</span>
                      {cannabinoidInfo && (
                        <span className="recipe-selector-item-info">{cannabinoidInfo}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="confirm-dialog-actions">
          <button
            className="confirm-dialog-button confirm-dialog-cancel"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
